import { ActionTree } from 'vuex';
import { AxiosInstance, AxiosResponse } from 'axios';
import { RallyIntelligenceState, RootState } from '../types';
import { state as authState } from '../auth/auth';
import { handleAllErrors } from '@/utils/errorHandling';
import { decodeRallyPrompt, decodeRallyPrompts } from '@/utils/decoding';

const BASE = '/rally-intelligence';

export const actions: ActionTree<RallyIntelligenceState, RootState> = {

	/**
	 * Fetch Rally prompts for the current location.
	 *
	 * @param {string} [search]
	 * @return {Promise<void>}
	 */
	async fetchPrompts({ commit }, search?: string): Promise<void> {
		commit('SET_FETCH_PROMPTS_COMPLETE', false);
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligencePromptResponse[]> = await axiosInst.get(`${BASE}/prompts`, { params: { search } });
			commit('SET_PROMPTS', decodeRallyPrompts(data));
			commit('SET_FETCH_PROMPTS_COMPLETE', true);
		}
		catch (error) {
			commit('SET_FETCH_PROMPTS_COMPLETE', true);
			handleAllErrors(error);
		}
	},

	/**
	 * Create a new prompt.
	 *
	 * @param {RallyIntelligencePromptRequest} draft
	 * @return {Promise<RallyIntelligencePromptResponse | void>}
	 */
	async createPrompt({ commit }, draft: RallyIntelligencePromptRequest): Promise<RallyIntelligencePromptResponse | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligencePromptResponse> = await axiosInst.post(`${BASE}/prompts`, draft);
			const decoded = decodeRallyPrompt(data);
			commit('ADD_PROMPT', decoded);
			return decoded;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update an existing prompt.
	 *
	 * @param {{ id: number, draft: RallyIntelligencePromptRequest }} payload
	 * @return {Promise<RallyIntelligencePromptResponse | void>}
	 */
	async updatePrompt({ commit }, { id, draft }: { id: number; draft: RallyIntelligencePromptRequest }): Promise<RallyIntelligencePromptResponse | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligencePromptResponse> = await axiosInst.post(`${BASE}/prompts/${id}`, draft);
			const decoded = decodeRallyPrompt(data);
			commit('UPDATE_PROMPT', decoded);
			return decoded;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Delete a prompt.
	 *
	 * @param {number} id
	 * @return {Promise<void>}
	 */
	async deletePrompt({ commit }, id: number): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.delete(`${BASE}/prompts/${id}`);
			commit('REMOVE_PROMPT', id);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Duplicate a prompt.
	 *
	 * @param {number} id
	 * @return {Promise<RallyIntelligencePromptResponse | void>}
	 */
	async duplicatePrompt({ commit }, id: number): Promise<RallyIntelligencePromptResponse | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligencePromptResponse> = await axiosInst.post(`${BASE}/prompts/${id}/duplicate`);
			const decoded = decodeRallyPrompt(data);
			commit('ADD_PROMPT', decoded);
			return decoded;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Activate a prompt for the current location.
	 *
	 * @param {number} id
	 * @return {Promise<void>}
	 */
	async activatePrompt({ commit }, id: number): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.post(`${BASE}/prompts/${id}/activate`);
			commit('SET_ACTIVE_PROMPT', id);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch the Rally configuration.
	 *
	 * @return {Promise<void>}
	 */
	async fetchConfiguration({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligenceConfigurationResponse> = await axiosInst.get(`${BASE}/configuration`);
			commit('SET_CONFIGURATION', data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update the Rally configuration.
	 *
	 * @param {UpdateRallyIntelligenceConfigRequest} payload
	 * @return {Promise<void>}
	 */
	async updateConfiguration({ commit }, payload: UpdateRallyIntelligenceConfigRequest): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligenceConfigurationResponse> = await axiosInst.post(`${BASE}/configuration`, payload);
			commit('SET_CONFIGURATION', data);
			if (payload.active_prompt_id !== undefined && data.active_prompt_id !== null) {
				commit('SET_ACTIVE_PROMPT', data.active_prompt_id);
			}
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Store an API key for a provider.
	 *
	 * @param {SetProviderCredentialRequest} payload
	 * @return {Promise<void>}
	 */
	async setProviderCredential({ dispatch }, payload: SetProviderCredentialRequest): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.put(`${BASE}/configuration/credentials`, payload);
			await dispatch('fetchConfiguration');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Test connection to the configured provider.
	 *
	 * @return {Promise<RallyIntelligenceTestConnectionResponse | void>}
	 */
	async testConnection(): Promise<RallyIntelligenceTestConnectionResponse | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligenceTestConnectionResponse> = await axiosInst.get(`${BASE}/configuration/test-connection`);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Fetch monthly token usage.
	 *
	 * @return {Promise<void>}
	 */
	async fetchUsage({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data }: AxiosResponse<RallyIntelligenceUsageResponse> = await axiosInst.get(`${BASE}/usage`);
			commit('SET_USAGE', data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	}
};
